import type { Nullable } from "@iot-platform/core";
import type {
    UserCreateBody,
    UserDto,
    UserEditBody,
    UsersDto,
} from "@user/data/dto/user.dto";
import { UsersDtoSchema } from "@user/data/dto/user.dto";
import type {
    User,
    UserCreate,
    UserCreateToSpatialLevel,
    UserEdit,
    UserResume,
} from "@user/domain/models/user.model";
import {
    UserCreateToSpatialLevelSchema,
    UserResumeSchema,
    UserSchema,
    Users,
} from "@user/domain/models/user.model";
import { injectable } from "inversify";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import type * as z from "zod";

export interface UserCreateToSpatialLevelFormInput {
    username: string;
    email: string;
    spatialLevelIds: number[];
    fieldTech: boolean;
}

@injectable()
export class UserMapper {
    map(userDto: UserDto): Nullable<User> {
        const userResult = UserSchema.safeParse({
            id: userDto.id,
            email: userDto.email,
        });

        if (!userResult.success) {
            console.error(userResult.error.issues);
            return null;
        }

        return userResult.data;
    }

    mapList(usersDto: UsersDto): Users {
        const usersDtoParsed = UsersDtoSchema.safeParse(usersDto);

        if (!usersDtoParsed.success) {
            console.error(usersDtoParsed.error.issues);
            return Users.empty();
        }

        const users = usersDtoParsed.data
            .map((userDto) => this.map(userDto))
            .filter((user): user is User => user !== null);

        return Users.create({
            items: users,
            count: users.length,
        }).unwrapOr(Users.empty());
    }

    mapToCreateBody(createUser: UserCreate): UserCreateBody {
        return {
            object: {
                email: createUser.email,
                password: createUser.password,
                field_tech: createUser.fieldTech,
            },
        };
    }

    mapToEditBody(editUser: UserEdit): UserEditBody {
        return {
            object: {
                id: editUser.id,
                email: editUser.email,
                password: editUser.password,
                field_tech: editUser.fieldTech,
            },
        };
    }

    mapResume(userDto: UserDto): Nullable<UserResume> {
        const userResult = UserResumeSchema.safeParse({
            id: userDto.id,
            email: userDto.email,
        });

        if (!userResult.success) {
            console.error(userResult.error.issues);
            return null;
        }

        return userResult.data;
    }

    mapFormToCreateToSpatialLevel(
        user: UserCreateToSpatialLevelFormInput,
    ): Result<UserCreateToSpatialLevel, z.ZodError<UserCreateToSpatialLevel>> {
        const maybeSpatialLevelUpdate =
            UserCreateToSpatialLevelSchema.safeParse({
                username: user.username,
                email: user.email,
                spatialLevelIds: user.spatialLevelIds,
                fieldTech: user.fieldTech,
            } satisfies UserCreateToSpatialLevel);

        return maybeSpatialLevelUpdate.success
            ? ok(maybeSpatialLevelUpdate.data)
            : err(maybeSpatialLevelUpdate.error);
    }
}
